import { Create, useForm } from '@refinedev/antd';
import { HttpError } from '@refinedev/core';
import { Form, Input, DatePicker } from 'antd';
import type { Dayjs } from 'dayjs';
import type { Conference } from '@viq-admin/types';
import { RequireCan } from '../../components/RequireCan';

/**
 * Create a conference (global — not tenant-scoped; grants attach tenants
 * later on the show shell). Dates are entered with AntD `DatePicker` (Dayjs)
 * and serialised to `YYYY-MM-DD`; `endDate` must be on or after `startDate`.
 * `manage Conference` gates the whole page via `RequireCan`.
 *
 * `useForm`'s TVariables is the API body shape (string dates, nullable
 * location); the `<Form>` `onFinish` callback receives the raw Dayjs form
 * values and maps them to that body — read directly, no cast.
 */
interface CreateConferenceInput {
  name: string;
  location?: string | null;
  startDate: string;
  endDate: string;
}

interface FormShape {
  name: string;
  location?: string;
  startDate: Dayjs;
  endDate: Dayjs;
}

export function ConferenceCreate() {
  const { formProps, saveButtonProps, onFinish } = useForm<
    Conference,
    HttpError,
    CreateConferenceInput
  >({ resource: 'conferences', action: 'create' });

  return (
    <RequireCan I="manage" a="Conference">
      <Create saveButtonProps={saveButtonProps}>
        <Form
          {...formProps}
          layout="vertical"
          onFinish={(values) => {
            // `values` are the raw Dayjs pickers; map to the string API body.
            const v = values as unknown as FormShape;
            return onFinish({
              name: v.name,
              location: v.location ?? null,
              startDate: v.startDate.format('YYYY-MM-DD'),
              endDate: v.endDate.format('YYYY-MM-DD'),
            });
          }}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Name is required' }]}
            extra="Choose a name that alphabetizes well, e.g. 2026-Infocom-Atlanta."
          >
            <Input maxLength={200} placeholder="2026-Infocom-Atlanta" />
          </Form.Item>
          <Form.Item label="Location" name="location">
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item
            label="Start date"
            name="startDate"
            rules={[{ required: true, message: 'Start date is required' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            label="End date"
            name="endDate"
            dependencies={['startDate']}
            rules={[
              { required: true, message: 'End date is required' },
              ({ getFieldValue }) => ({
                validator(_, value: Dayjs | undefined) {
                  const start = getFieldValue('startDate') as Dayjs | undefined;
                  if (!value || !start) return Promise.resolve();
                  return value.isBefore(start, 'day')
                    ? Promise.reject(
                        new Error('End date must be on or after start'),
                      )
                    : Promise.resolve();
                },
              }),
            ]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Create>
    </RequireCan>
  );
}
